import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { debounceTime, distinctUntilChanged, forkJoin, map, Observable, of, Subject, Subscription, switchMap, tap, take } from 'rxjs';
import { InfoDialogComponent, TrivUIToolsLibModule }  from '@trivelope/triv-ui-tools-lib';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatIconModule } from '@angular/material/icon';
import { User } from 'src/app/classes/user';
import { ChatService } from 'src/app/services/chat.service';
import { Chat, ChatFile, ChatType, Message } from 'src/app/classes/chat';
import { DatePipe, formatDate } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ChatApprovalComponent } from './chat-approval.component';
import { animate, style, transition, trigger } from '@angular/animations';
import { FileService } from 'src/app/services/file.service';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { NewCountersService } from 'src/app/services/new-counters.service';
import { AvatarsService } from 'src/app/services/avatars.service';
import { AvatarCircleComponent } from '../avatar-circle/avatar-circle.component';


@Component({
	selector: 'chat',
	templateUrl: './chat.component.html',
	styleUrls: [ './chat.component.scss' ],
	standalone: true,
	imports: [
		FormsModule,
		MatButtonModule, MatCheckboxModule,
		MatIconModule,
		MatMenuModule,
		MatProgressBarModule, MatProgressSpinnerModule,
		DatePipe,
		RouterModule,
		MatDialogModule,
		TrivUIToolsLibModule,
		ChatApprovalComponent,
		AvatarCircleComponent,
	],
	animations: [
		trigger( 'myInsertRemoveTrigger', [
			transition( ':enter', [ style({ 'max-width': '0px' }), animate( '300ms', style({ 'max-width': 'calc(100% - 450px)' }) ) ]),
			transition( ':leave', [ animate( '300ms', style({ 'max-width': '0px' }) ) ]),
		]),
	],
})
export class ChatComponent implements OnInit, OnDestroy {


	bApprovalNeeded = true;
	bFirstLoading = true;
	bFirstLoadingError = false;
	bLoadingFirstMessages = false;
	bLoadingNextMessages = false;
	bLoadingMessagesError = false;
	bCommInProgress = false;
	subscriptions = new Subscription();
	bSending = false;
	bSendingError = false;
	bPayloadTooBig = false;
	msgCurrentPage = 0;
	msgCurrentPageMax: number|null = null;

	contacts: User[] = [];
	contactNames: { [key:string]: string } = {};
	chatList: Chat[] = [];
	selectedChat: Chat|null = null;
	dayDisplay: { [key:string]: string } = {};

	myUserId = "";

	typedMessage = "";
	@ViewChild('textAreaInput') textAreaInput!: ElementRef<HTMLTextAreaElement>;

	strSearchFilter = "";
	chatFilterSubject = new Subject<string>();
	chatFilterObs = this.chatFilterSubject.asObservable();

	readonly filesizeMaxMB = 50;
	filesToUpload: ChatFile[] = [];

	chatIdToLoad = "";

	ChatType = ChatType;

	bChatAdmin = false;

	messageBeingRepliedTo: Message|null = null;

	chatMoreResults = false;
	chatCurrentPage = 0;
	chatNextPage = 0;
	chatPageLoading = false;

	readonly REFRESH_TIMEOUT_MS = 5000;	// every 5000ms
	timeoutRefresh = 0;

	bNotifications = false;
	bNotificationSavingInProgress = false;


	constructor(
		private chatService: ChatService,
		private authenticationService: AuthenticationService,
		private matDialog: MatDialog,
		private router: Router,
		private activatedRoute: ActivatedRoute,
		private fileService: FileService,
	private newCountersService: NewCountersService,
	private avatarsService: AvatarsService,
	) {}


	ngOnInit() {

		this.subscriptions.add(
			this.activatedRoute.queryParams.subscribe( params => {
				if( this.bFirstLoading ) {
					this.chatIdToLoad = params['chatId'] || "";
				}
			})
		);

		this.subscriptions.add(
			this.chatFilterObs.pipe(
				debounceTime( 400 ),
				distinctUntilChanged(),
				switchMap( str => {

					this.chatCurrentPage = 0;
					this.chatMoreResults = false;
					this.bCommInProgress = true;

					return this.getChatList().pipe(
						tap( _ => {
							this.bCommInProgress = false;
						})
					);

				} ),
			).subscribe()
		);


		const myUser = this.authenticationService.getCurrentUser();
		this.bApprovalNeeded = !myUser.ebeChatNutzungsbedingungBestaetigt;
		this.myUserId = myUser.ebeId;

		forkJoin( {
			contacts: this.chatService.getContacts(),
			chats: this.getChatList(),
		} )
		.subscribe( answers => {

			if( !answers || !answers.contacts || !answers.chats ) {
				this.bFirstLoading = false;
				this.bFirstLoadingError = true;
				return;
			}

			this.contacts = answers.contacts
				.sort( (a,b) => {

					const arrA: string[] = [];
					if( a.ebeVorname ) arrA.push( a.ebeVorname );
					if( a.ebeName ) arrA.push( a.ebeName );
					if( arrA.length ) arrA.push( a.ebeId );
					const nameA = arrA.join( " " );
					const arrB: string[] = [];
					if( b.ebeVorname ) arrB.push( b.ebeVorname );
					if( b.ebeName ) arrB.push( b.ebeName );
					if( arrB.length ) arrB.push( b.ebeId );
					const nameB = arrB.join( " " );

					return nameA.localeCompare( nameB );

				} );


			for( const contact of this.contacts ) {
				const arr: string[] = [];
				if( contact.ebeVorname ) arr.push( contact.ebeVorname );
				if( contact.ebeName ) arr.push( contact.ebeName );
				if( !arr.length ) arr.push( contact.ebeId );
				this.contactNames[ contact.ebeId ] = arr.join( " " ) || contact.ebeId;
			}

			this.bFirstLoading = false;

			if( this.chatIdToLoad ) {
				const chat = this.chatList.find( c => c.id == this.chatIdToLoad );
				if( chat ) this.toggleMessages( chat );
			}

			this.startAutoRefresh();

		});

	}

	ngOnDestroy() {

		this.subscriptions.unsubscribe();

		this.stopAutoRefresh();

	}

	private startAutoRefresh() {

		this.stopAutoRefresh();
		this.timeoutRefresh = window.setTimeout( () => this.refreshChatListAndMessages(), this.REFRESH_TIMEOUT_MS );

	}

	private stopAutoRefresh() {

		if( this.timeoutRefresh ) {
			window.clearTimeout( this.timeoutRefresh );
			this.timeoutRefresh = 0;
		}

	}

	toggleMessages( chat: Chat ) {

		this.stopAutoRefresh();

		if( this.selectedChat && (chat.id == this.selectedChat.id) ) {
			this.selectedChat = null;
			this.router.navigate(
				[],
				{ relativeTo: this.activatedRoute, queryParams: {} }
			);
			return;
		}

		this.router.navigate(
			[],
			{ relativeTo: this.activatedRoute, queryParams: { chatId: chat.id } }
		);

		this.selectedChat = chat;

		if( this.selectedChat.bNewMsg ) {
			this.newCountersService.decrementNewChats();
		}
		this.selectedChat.bNewMsg = false;

		this.bNotifications = chat.bNotifications;
		this.bNotificationSavingInProgress = false;

		this.msgCurrentPage = 0;
		this.msgCurrentPageMax = null;

		this.typedMessage = "";
		this.filesToUpload = [];

		this.bChatAdmin = false;

		const obss = {
			messages: this.chatService.getMessages( chat.id ),
			chatGroup: (chat.chatType == ChatType.userGroup)
				? this.chatService.getChatGroup( chat.sourceId )
				: of(null)
		};

		this.bLoadingFirstMessages = true;
		this.bCommInProgress = true;

		forkJoin( obss ).subscribe( results => {

			this.bLoadingFirstMessages = false;
			this.bCommInProgress = false;

			if( !results || !results.messages || !this.selectedChat ) {
				console.warn( "error while fetching the first messages", results )
				this.bLoadingMessagesError = true;
				return;
			}

			this.bLoadingMessagesError = false;
			this.selectedChat.messages = results.messages.sort( (a,b) => {
				if( a.timestamp === b.timestamp ) return 0;
				if( a.timestamp === null ) return -1;
				if( b.timestamp === null ) return 1;
				return a.timestamp - b.timestamp;
		 	} );


			this.generateDayLabelsInChat();
			this.scrollToLastMessage();

			setTimeout( () => {
				const elems = window.document.getElementsByClassName( "chatInfoBar" );
				if( elems && elems.length ) {
					elems[0].scrollIntoView();
				}
			}, 1 );

			if( results.chatGroup ) {
				this.bChatAdmin = results.chatGroup.bAdmin || this.authenticationService.isAdmin();
			}

			this.startAutoRefresh();

		});

	}

	private generateDayLabelsInChat() {

		this.dayDisplay = {};
		if( !this.selectedChat || !this.selectedChat.messages || !this.selectedChat.messages.length ) return;

		const dt = new Date();
		const last7days: string[] = [];
		last7days.push( formatDate( dt.getTime(), "dd.MM.YYYY", "de-DE" ) );
		const oneDay = 24*3600*1000;
		const last7Display = [ "Heute" ];
		for( let n = 0; n < 6; n++ ) {
			dt.setTime( dt.getTime() - oneDay );
			last7days.push( formatDate( dt.getTime(), "dd.MM.YYYY", "de-DE" ) );
			last7Display.push( formatDate( dt.getTime(), "EEEE", "de-DE" ) );
		}
		last7Display[1] = "Gestern";

		let lastDay = "";
		for( const message of this.selectedChat.messages ) {

			if( message.timestamp === null ) continue;

			const day = formatDate( message.timestamp, "dd.MM.YYYY", "de-DE" );
			if( day != lastDay ) {

				for( let n = 0; n < last7days.length; n++ ) {
					if( day == last7days[n] ) {
						this.dayDisplay[ message.id ] = last7Display[n];
						break;
					}
				}

				if( !this.dayDisplay[ message.id ] ) {
					this.dayDisplay[ message.id ] = formatDate( message.timestamp, "dd.MM", "de-DE" );
				}

				lastDay = day;
			}

		}

	}

	clickSendMessage() {

		if( !this.typedMessage ) {
			this.textAreaInput.nativeElement.focus();
			return;
		}
		if( this.bSending ) return;
		if( !this.selectedChat ) return;

		const message = new Message();
		message.text = this.typedMessage;
		message.chatId = this.selectedChat.id;
		message.senderId = this.myUserId;

		if( this.messageBeingRepliedTo ) {
			message.msgRepliedToId = this.messageBeingRepliedTo.id;
		}

		this.bSending = true;
		this.bCommInProgress = true;

		this.chatService.sendMessage(
			message,
			this.filesToUpload
		).subscribe( msg => {

			this.bSending = false;
			this.bSendingError = !msg;
			this.bCommInProgress = false;
			if( !msg ) return;

			this.bPayloadTooBig = (msg == 413);
			if( msg == 413 ) return;

			if( !this.selectedChat ) return;

			this.messageBeingRepliedTo = null;

			this.selectedChat.messages.push( msg );
			this.generateDayLabelsInChat();
			this.selectedChat.lastMsg = msg.text;
			this.selectedChat.lastMsgTms = msg.timestamp;
			this.selectedChat.lastMsgTmsStr = this.convertTmsToStr( msg.timestamp )

			this.typedMessage = "";
			this.filesToUpload = [];
			window.setTimeout( () => {
				this.autoGrowTextArea();
			}, 1 );

			this.scrollToLastMessage();

		});

	}

	private convertTmsToStr( tms: number|null ): string {

		if( tms === null ) return "";
		if( tms === undefined ) return "";

		const dt = new Date();
		dt.setTime( tms );
		dt.setHours( 0, 0, 0, 0 );
		const dtNow = new Date();
		dtNow.setHours( 0, 0, 0, 0 );
		if( dt.getTime() == dtNow.getTime() ) return formatDate( tms, "HH:mm", "de-DE" );

		return formatDate( tms, "dd/MM", "de-DE" );

	}

	private scrollToLastMessage() {

		const msgPanel = document.getElementsByClassName( "msgPanel" )[0];
		if( !msgPanel ) return;

		setTimeout( () => {
			msgPanel.scrollTo( 0, msgPanel.scrollHeight );
			// msgPanel.scrollTo( { top: msgPanel.scrollHeight, left: 0, behavior: "smooth" } );
		}, 1 );

	}

	chatFilter( event: Event ) {
		this.chatFilterSubject.next( this.strSearchFilter );
	}

	clickChatsSortChrono() {

		this.chatList.sort( (a,b) => {
			if( a === b ) return 0;
			if( a.lastMsgTms === null ) return -1;
			if( b.lastMsgTms === null ) return 1;
			return a.lastMsgTms - b.lastMsgTms;
		});

	}

	clickSortChatsAlpha() {

		this.chatList.sort( (a,b) => {
			return a.title.localeCompare( b.title );
		});

	}

	onFileDroppedInComponent( fl: any ) {
		this.onFileAdd( fl );
	}

	onFileInputAdd( e: EventTarget | null ) {
		if( e && (<HTMLInputElement>e).files )
			this.onFileAdd( (<HTMLInputElement>e).files || new FileList() );
	}

	onFileAdd( fl: FileList ) {

		console.log( 'FILE ADD', fl )
		if( this.bSending ) return;
		if( !fl || !fl.length ) return;

		const files: File[] = []
		for( let i = 0; i < fl.length; i++ ) {
			const b = this.checkFile( fl[i] );
			if( !b ) return;
			files.push( fl[i] );
		}

		this.filesToUpload.push( ...files.map( (f,i) => {
			const cf = new ChatFile();
			cf.id = "fileToUpload_" + (this.filesToUpload.length+i);
			cf.file = f;
			cf.fileName = f.name;
			return cf;
		}) );

	}

	private checkFile( file: File ): boolean {

		/*
		const arr = file.name.split('.');
		const extension = '.' + arr[arr.length - 1].toLocaleLowerCase();
		const ftA = this.filetypesAccepted;
		if( ftA && ftA.length && !ftA.includes(extension) ) {
			console.log( 'filetype not accepted', extension )
			this.matSnackBar.open(
				'Es sind nur die Dateitypen zulässig: ' + ftA.join(', '),
				'Fehler',
				{ duration: 5000 }
			);
			return false;
		}
		*/
		if( file.size >= this.filesizeMaxMB * 1024 * 1024 ) {
			console.log( 'size too big' );
			this.matDialog.open( InfoDialogComponent, {
				width: '350px',
				data: 'Die maximale Dateigröße beträgt ' + this.filesizeMaxMB + ' MB'
			});
			return false;
		}
		return true;

	}

	cancelUpload( fileId: string ) {

		const i = this.filesToUpload.findIndex( f => f.id == fileId );
		if( i != -1 ) {
			this.filesToUpload.splice( i, 1 );
		}

	}

	autoGrowTextArea() {

		if( !this.textAreaInput ) return;

		const textArea = this.textAreaInput.nativeElement;
		if( textArea.value != '' ) {
			const s = window.getComputedStyle(textArea);
			const tTop = +s.paddingTop.split('px').join('');
			const tBottom = +s.paddingBottom.split('px').join('');
			const height = textArea.scrollHeight - tTop - tBottom;
			textArea.style.height = height + 'px'; // Adjust height based on content
		} else {
			textArea.style.height = 'auto'; // Reset the height so that shrinking works properly
		}

	}

	onKeydownEvent( event: KeyboardEvent ) {

		if( event.key == "Enter" ) {
			if( event.altKey ) {
				this.typedMessage += '\n';
			}
			else {
				event.preventDefault();
				this.clickSendMessage();
			}
		}

	}

	clickReplyTo( message: Message ) {

		if( !message ) return;
		if( this.bCommInProgress ) return;

		this.messageBeingRepliedTo = message;
		if( this.textAreaInput ) {
			this.textAreaInput.nativeElement.focus();
		}

	}

	cancelReplyTo() {

		if( this.bCommInProgress ) return;

		this.messageBeingRepliedTo = null;

	}

	messagesScrolled( event: Event ) {

		if( this.bCommInProgress ) return;
		if( !this.selectedChat ) return;
		if( this.msgCurrentPageMax !== null ) return;
		if( !event || !event.target ) return;
		const msgPanel = event.target as HTMLElement;
		if( msgPanel.scrollTop > 200 ) return;

		this.bCommInProgress = true;
		this.bLoadingNextMessages = true;

		this.chatService.getMessages( this.selectedChat?.id, this.msgCurrentPage+1 ).subscribe( messages => {

			this.bLoadingNextMessages = false;

			if( !messages || !this.selectedChat ) {
				console.warn( "error while fetching the messages", messages )
				this.bLoadingMessagesError = true;
				return;
			}

			this.bLoadingMessagesError = false;

			if( !messages.length ) {
				this.msgCurrentPageMax = this.msgCurrentPage;
				return;
			}

			this.msgCurrentPage++;

			this.selectedChat.messages = [
				...messages.sort( (a,b) => {
					if( a.timestamp === b.timestamp ) return 0;
					if( a.timestamp === null ) return -1;
					if( b.timestamp === null ) return 1;
					return a.timestamp - b.timestamp;
				} ),
				...this.selectedChat.messages,
			];

			this.generateDayLabelsInChat();

		});

	}

	switchChatPage( n: number ) {

		if( this.bCommInProgress ) return;

		const nextPage = this.chatCurrentPage + n;
		if( nextPage < 0 ) return;
		if(  ( nextPage > this.chatCurrentPage ) && !this.chatMoreResults  ) return;

		this.chatNextPage = nextPage;

		this.stopAutoRefresh();

		this.bCommInProgress = true;
		this.getChatList().subscribe( b => {
			this.bCommInProgress = false;
			if( b ) {
				this.startAutoRefresh();
			}
		});

	}

	private getChatList(): Observable<boolean> {

		this.chatPageLoading = true;

		const obs = this.strSearchFilter
			? this.chatService.searchChats( this.strSearchFilter, this.chatNextPage )
			: this.chatService.getChats( this.chatNextPage );

		return obs.pipe(

			map( answer => {

				this.chatPageLoading = false;

				if( !answer ) {
					this.bFirstLoadingError = true;
					return false;
				}

				this.chatMoreResults = answer.bMoreResults;

				// there might be no results because the totalPages given by the Backend might be higher than the reality
				if( answer.chats.length || (this.chatNextPage < this.chatCurrentPage) ) {
					this.chatList = answer.chats;
					this.preloadChatAvatars(this.chatList);
					this.chatCurrentPage = this.chatNextPage;
				}

				return true;

			} )

		);

	}


	private refreshChatListAndMessages() {

		this.bCommInProgress = true;
		forkJoin( {
			chatList: this.getChatList(),
			chatMessages: this.refreshMessages()
		} ).subscribe( result => {

			this.bCommInProgress = false;
			if( result && result.chatList && result.chatMessages ) {
				this.startAutoRefresh();
			}

		});

	}

	private refreshMessages(): Observable<boolean> {

		if( !this.selectedChat ) return of(true);

		return this.chatService.getMessages( this.selectedChat.id ).pipe(

			map( messages => {

				if( !messages || !this.selectedChat ) {
					console.warn( "error while fetching the messages", messages )
					this.bLoadingMessagesError = true;
					return false;
				}

				this.bLoadingMessagesError = false;

				let bNewMsg = false;
				for( const message of messages ) {

					if( !this.selectedChat.messages.find( m => m.id == message.id ) ) {
						this.selectedChat.messages.push( message );
						bNewMsg = true;
					}

				}

				this.selectedChat.messages.sort( (a,b) => {
					if( a.timestamp === b.timestamp ) return 0;
					if( a.timestamp === null ) return -1;
					if( b.timestamp === null ) return 1;
					return a.timestamp - b.timestamp;
				} );

				if( bNewMsg ) this.scrollToLastMessage();

				return true;

			})

		);

	}

	private preloadChatAvatars(chats: Chat[]): void {
		const ids = chats
			.filter(chat => chat.chatType === ChatType.direct && !!chat.sourceId)
			.map(chat => chat.sourceId);
		if (!ids.length) return;
		const uniqueIds = Array.from(new Set(ids));
		this.subscriptions.add(
			this.avatarsService.ensureMinis(uniqueIds).pipe(take(1)).subscribe()
		);
	}

	clickDownload( file: ChatFile ) {

		this.fileService.downloadFileByIdObs( file.id, file.fileName ).subscribe( b => {
			console.log( "file download", b );
		});

	}

	onNotificationClick() {

		if( !this.selectedChat ) return;
		if( this.bNotificationSavingInProgress ) return;

		this.bNotificationSavingInProgress = true;
		this.chatService.setChatNotification( this.selectedChat.id, this.bNotifications ).subscribe( b => {

			this.bNotificationSavingInProgress = false;
			if( !b ) {
				this.bNotifications = !this.bNotifications;
				this.matDialog.open( InfoDialogComponent, {
					width: '350px',
					data: 'Beim Speichern der Benachrichtigungseinstellung ist ein Fehler aufgetreten'
				});
			}
			else {
				if( this.selectedChat ) {
					this.selectedChat.bNotifications = this.bNotifications;
				}
			}

		} );

	}

}






